import { Box, Paper, Typography, useTheme } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { HelpTooltip } from '@/shared';
import { ITOW_CHART_RAMP } from '@/theme/theme';

export interface ActivityDatum {
  /** Axis label for the bucket, already localised (e.g. a weekday short name). */
  label: string;
  count: number;
}

export interface ActivityBarChartProps {
  titleId: string;
  title: string;
  /** Optional `(i)` tooltip beside the title. */
  helpText?: string;
  helpAriaLabel?: string;
  data: ActivityDatum[];
  loading?: boolean;
}

/**
 * A single-series, time-ordered bar chart — the Driver Activity panel on the
 * admin dashboard and Claims Filed on the customer's.
 *
 * Extracted when the second one arrived rather than copied: the colour rules
 * below are easy to get subtly wrong twice.
 *
 * Colour is an **ordinal ramp** — one hue, seven monotone lightness steps,
 * indexed by the bucket's position in the window. Deliberately not:
 *
 *  - a categorical palette. There is one series; seven hues would invent seven
 *    identities and read as a rainbow.
 *  - a value ramp. Shading each bar by its own height re-encodes what height
 *    already shows and spends the only free channel on nothing.
 *
 * Days *are* a sequence — reordering them changes the meaning — so an ordinal
 * ramp is the encoding that fits, and it gives the week a visible direction.
 * Dark mode uses its own steps, reversed, so the newest bucket is the brightest
 * mark against a dark surface rather than the murkiest; it is not a flip of the
 * light ramp. Both ramps are validated in `theme.ts`.
 *
 * No legend: one series needs none, the heading names it.
 */
export default function ActivityBarChart({
  titleId,
  title,
  helpText,
  helpAriaLabel,
  data,
  loading = false,
}: ActivityBarChartProps) {
  const theme = useTheme();
  const ramp =
    theme.palette.mode === 'dark'
      ? [...ITOW_CHART_RAMP.dark].reverse()
      : [...ITOW_CHART_RAMP.light];

  return (
    <Paper sx={{ mt: 3, p: 3 }} aria-busy={loading} aria-labelledby={titleId} role="region">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
        <Typography id={titleId} variant="subtitle1" component="h3" sx={{ fontWeight: 'medium' }}>
          {title}
        </Typography>
        {helpText !== undefined && (
          <HelpTooltip title={helpText} ariaLabel={helpAriaLabel ?? helpText} placement="top" />
        )}
      </Box>
      <BarChart
        height={300}
        margin={{ top: 16, right: 16, bottom: 32, left: 40 }}
        series={[
          {
            data: data.map((d) => d.count),
            valueFormatter: (v) => String(v ?? 0),
          },
        ]}
        xAxis={[
          {
            scaleType: 'band',
            data: data.map((d) => d.label),
            colorMap: {
              type: 'ordinal',
              values: data.map((d) => d.label),
              colors: ramp.slice(0, Math.max(data.length, 1)),
            },
            tickLabelStyle: { fill: theme.palette.text.secondary, fontSize: 11 },
          },
        ]}
        yAxis={[{ tickLabelStyle: { fill: theme.palette.text.secondary, fontSize: 11 } }]}
        borderRadius={4}
        slotProps={{ legend: { hidden: true } }}
        sx={{
          '& .MuiChartsAxis-line, & .MuiChartsAxis-tick': { stroke: theme.palette.divider },
          '& .MuiChartsGrid-line': {
            stroke: theme.palette.divider,
            strokeDasharray: 'none',
          },
        }}
        grid={{ horizontal: true }}
      />
    </Paper>
  );
}
